import React, { useCallback, useEffect, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { LockKeyhole, Signature, LockKeyholeOpen } from 'lucide-react';
import { formatDate } from 'utils/helpers';

const SignaturePad = ({ setSignature, signature, error }) => {
  const sigCanvas = useRef(null);
  const isFile = signature instanceof File;
  const isSavedSignature = signature && !isFile;

  const handleClear = () => {
    if (sigCanvas.current)
      sigCanvas.current.clear();

    setSignature('signature', '');
  };

  const handleEnd = useCallback(() => {
    if (!sigCanvas.current) return;

    sigCanvas.current.getCanvas().toBlob((blob: Blob | null) => {
      if (!blob) return;

      const file = new File([blob], 'signature.png', { type: 'image/png' });
      setSignature('signature', file);
    }, 'image/png');
  }, [setSignature]);

  useEffect(() => {
    if (sigCanvas.current && signature && sigCanvas.current.isEmpty()) {
      sigCanvas.current.fromDataURL(signature);
    }
  }, [signature]);

  return (
    <div className="card border border-base-200 shadow-sm my-5">
      <div className="flex flex-row items-center justify-between p-5 border-b border-base-200">
        <div className="flex flex-row items-center">
          <div className="bg-blue-100 rounded-lg p-2 mr-3">
            <Signature size={18} strokeWidth={2} className="text-blue-600" />
          </div>

          <p className='font-semibold text-md text-base-content'>
            {isSavedSignature ? 'Saved signature' : 'Draw signature'}
          </p>
        </div>

        {signature &&
          <button
            type="button"
            onClick={() => handleClear()}
            className="text-sm text-blue-500 hover:text-blue-700 font-medium"
          >
            Clear
          </button>
        }
      </div>

      <div className="card-body flex-col p-5">
        <div className="border border-gray-300 rounded-md bg-gray-50 overflow-hidden flex justify-center items-center h-40">

          {/* Conditionally render the image OR the canvas */}
          {isSavedSignature ?
            <img
              src={signature}
              alt="Saved Signature"
              className="max-h-full max-w-full object-contain pointer-events-none"
            />
            :
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ className: 'w-full h-full cursor-crosshair' }}
              onEnd={handleEnd}
            />
          }
        </div>

        {signature && (
          <div className="flex flex-row items-center justify-between mt-3 gap-1">
            {
              isSavedSignature ? <LockKeyhole size={14} strokeWidth={3} className="text-green-600" />
                : <LockKeyholeOpen size={14} strokeWidth={3} className="text-green-600" />
            }
            <p className="text-gray-500 text-xs">
              {
                isSavedSignature ? "Previously signed electronically"
                  : `Signed electronically on ${formatDate(Date.now(), 'short')}`
              }
            </p>
          </div>
        )}

        {error && <p className="text-error text-xs mt-1 ml-1">{error}</p>}
      </div>
    </div>
  )
}

export default SignaturePad;
